import type {
    ConsumeExchangeTokenResponse,
    CreateExchangeTokenResponse,
    IdentityChangePasswordPayload,
    IdentityChangePasswordResponse,
    IdentityErrorResponse,
    IdentityResetPasswordPayload,
    IdentityResetPasswordResponse,
    IdentityRetrieveRegistrationTokenParams,
    IdentityRetrieveRegistrationTokenResponse,
} from '@libs/common-types';
import { getCountry, getLocale, isLocaleCA, ResponseWithParsedData } from '@libs/core';
import { IDENTITY_ENDPOINTS } from '../constants';
import { getENV } from '@libs/common-next';
import { identityClient } from '../clients/api-proxy-browser-client';

type ApiClientResponse<T> = T | Error | ResponseWithParsedData<T>;

const consumerHeaderValue = isLocaleCA() ? 'DemandWareCA' : 'DemandWareUS';

export async function createExchangeToken() {
    return await identityClient.get<CreateExchangeTokenResponse>(IDENTITY_ENDPOINTS.createExchangeToken, {
        useOldFetch: true,
    });
}

export async function consumeExchangeToken(exchangeToken: string) {
    return await identityClient.get<ConsumeExchangeTokenResponse>(IDENTITY_ENDPOINTS.consumeExchangeToken, {
        headers: {
            'x-petm-exchange-token': exchangeToken,
        },
        additionalSuccessCodes: [401],
        useOldFetch: true,
    });
}

export async function retrieveRegistrationToken(
    params: IdentityRetrieveRegistrationTokenParams
): Promise<ApiClientResponse<IdentityRetrieveRegistrationTokenResponse>> {
    const { code, grantType, redirectUri, source } = params;

    const query = `code=${encodeURIComponent(code)}&grantType=${encodeURIComponent(
        grantType
    )}&redirectUri=${encodeURIComponent(redirectUri)}&source=${encodeURIComponent(source)}`;

    const url = `/v1/authentication/retrieveRegistrationToken?${query}`;

    return await identityClient.get<IdentityRetrieveRegistrationTokenResponse>(url, {
        headers: {
            'Content-Type': 'application/json',
            'Consumer-Country': getCountry(),
        },
    });
}

export async function resetPassword(
    body: { email: string; redirectUri: string },
    reCaptchaToken?: string
): Promise<ApiClientResponse<IdentityResetPasswordResponse | IdentityErrorResponse | null>> {
    const url = IDENTITY_ENDPOINTS.resetPassword(consumerHeaderValue);

    const payload: IdentityResetPasswordPayload = {
        email: body.email,
        locale: getLocale(),
        flow: getENV('NEXT_PUBLIC_IDENTITY_API_WEB_FLOW'),
        flowVersion: getENV('NEXT_PUBLIC_IDENTITY_API_WEB_FLOW_VERSION'),
        janrainFormPayloadType: 'forgotPassword',
        redirectUri: body.redirectUri,
        reCaptchaToken: reCaptchaToken,
    };

    return await identityClient.post<
        IdentityResetPasswordResponse | IdentityErrorResponse | null,
        IdentityResetPasswordPayload
    >(url, {
        headers: {
            'Consumer-Country': getCountry(),
            'Content-Type': 'application/json',
            'X-Petm-Account-Skip-Recaptcha': 'false',
        },
        body: payload,
        additionalSuccessCodes: [400, 500],
    });
}

export async function changePassword(
    params: {
        newPassword: string;
        newPasswordConfirm: string;
        isForgot: boolean;
        currentPassword?: string;
    },
    accessToken: string
): Promise<ApiClientResponse<IdentityChangePasswordResponse | IdentityErrorResponse | null>> {
    const consumerHeaderValue = isLocaleCA() ? 'DemandWareCA' : 'DemandWareUS';

    const url = IDENTITY_ENDPOINTS.changePassword(consumerHeaderValue);
    const currentPassword = params.isForgot ? '' : params.currentPassword || '';

    const changePasswordPayload: IdentityChangePasswordPayload = {
        accessToken: accessToken,
        currentPassword: currentPassword,
        newPassword: params.newPassword,
        newPasswordConfirm: params.newPasswordConfirm,
        locale: getLocale(),
        flow: getENV('NEXT_PUBLIC_IDENTITY_API_WEB_FLOW'),
        flowVersion: getENV('NEXT_PUBLIC_IDENTITY_API_WEB_FLOW_VERSION'),
        form: params.isForgot ? 'changePasswordFormNoAuth' : 'changePasswordForm',
        janrainFormPayloadType: 'passwordChange',
        responseType: 'token',
    };

    type ChangePasswordApiResponse = IdentityChangePasswordResponse | IdentityErrorResponse;

    return await identityClient.post<ChangePasswordApiResponse | null, IdentityChangePasswordPayload>(url, {
        headers: {
            'Consumer-Country': getCountry(),
            'Content-Type': 'application/json',
        },
        body: changePasswordPayload,
        additionalSuccessCodes: [400],
    });
}
